import { useCallback, useEffect, useRef, useState } from 'react';
import { analyticsRequests } from '../api/analyticsApi';

export default function useAnalyticsData(path, ttl = 60000, pendingTimeout = 0) {
  const [, render] = useState(0);
  const attempts = useRef(0);
  const [pollExpired, setPollExpired] = useState(false);
  const [refreshCycle, setRefreshCycle] = useState(0);
  useEffect(() => {
    if (!path) return undefined;
    const unsubscribe = analyticsRequests.subscribe(path, () => render(n => n + 1));
    attempts.current = 0;
    analyticsRequests.get(path, ttl).catch(() => {});
    return unsubscribe;
  }, [path, ttl]);
  const refresh = useCallback(() => {
    attempts.current = 0;
    setPollExpired(false);
    setRefreshCycle(n => n + 1);
    return path ? analyticsRequests.get(path, ttl, true) : Promise.resolve();
  }, [path, ttl]);
  const value = path ? analyticsRequests.snapshot(path) : {};
  useEffect(() => {
    if (!path || value.status !== 429 || !value.retry) return undefined;
    // Respect the server's retry window and spread retries from different tabs.
    const delay = Math.max(0, value.retry - Date.now()) + Math.floor(Math.random() * 2000);
    const timer = setTimeout(() => analyticsRequests.get(path, ttl, true).catch(() => {}), delay);
    return () => clearTimeout(timer);
  }, [path, value.status, value.retry, ttl]);
  useEffect(() => {
    if (path && value.expires === 0 && !value.refreshing && !(value.retry > Date.now())) analyticsRequests.get(path,ttl,true).catch(()=>{});
  }, [path, value.expires, value.refreshing, value.retry, ttl]);
  const pending = !!value.data?.pending || !!value.data?.refreshing || value.data?._cache?.state === 'stale';
  const followPending = !!value.data?.pending && /\/follows(?:-total)?\//.test(path || '');
  const maxAttempts = followPending ? 12 : 4;
  useEffect(() => {
    setPollExpired(false);
    if (!path || !pending || !pendingTimeout) return undefined;
    const deadline = setTimeout(() => setPollExpired(true), pendingTimeout);
    return () => clearTimeout(deadline);
  }, [path, pending, refreshCycle, pendingTimeout]);
  useEffect(() => {
    if (!path || !pending || pollExpired || attempts.current >= maxAttempts || value.error) return undefined;
    const timer = setTimeout(() => { attempts.current++; analyticsRequests.get(path,ttl,true).catch(()=>{}); }, followPending ? 5000 : 15000);
    return () => clearTimeout(timer);
  }, [path, pending, value.at, value.error, ttl, followPending, maxAttempts, pollExpired]);
  return { data: value.data || null, error: value.error || value.data?._cache?.refresh_error || null, loading: !!path && !value.data && !value.error,
    notice: value.notice || null, rateLimited: value.status === 429,
    refreshing: !pollExpired && (value.refreshing || (pending && attempts.current < maxAttempts && !value.error && !value.data?._cache?.refresh_error)),
    refreshDelayed: pending && pollExpired && !value.error && !value.data?._cache?.refresh_error,
    updatedAt: value.at || null, refresh };
}
